// ── Auth state machine (ported from UniTicket useAuth, plan §6) ──
// In-memory access token + silent /auth/refresh on mount (also covers the
// ?sso=ok landing after the Entra redirect). Multi-step local login:
// login → TOTP_SETUP | TOTP_VERIFY | PASSWORD_EXPIRED | AUTHENTICATED.
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AuthUser,
  LoginResponse,
  TotpSetupResponse,
  TotpConfirmResponse,
  TotpVerifyResponse,
  RecoveryVerifyResponse,
} from '@ext-portal/shared';
import { apiFetch, setAccessToken, ApiError } from '@/lib/api-client';

type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'totp_setup'; tempToken: string }
  | { status: 'totp_verify'; tempToken: string }
  | { status: 'password_expired'; tempToken: string }
  | { status: 'authenticated'; user: AuthUser };

interface AuthContextType {
  state: AuthState;
  user: AuthUser | null;
  /** Cross-publisher superuser (User.role). */
  isPlatformAdmin: boolean;
  /** Administers at least one publisher (derived from memberships) or is platform admin. */
  canPublish: boolean;
  login: (email: string, password: string) => Promise<void>;
  totpSetup: () => Promise<TotpSetupResponse>;
  totpConfirm: (code: string, secret: string) => Promise<string[]>;
  totpVerify: (code: string, rememberDevice?: boolean) => Promise<void>;
  recoveryVerify: (code: string, rememberDevice?: boolean) => Promise<number>;
  forceChangePassword: (newPassword: string) => Promise<void>;
  /** Abandon a mid-login step (2FA / setup / expired-password) back to the login form. */
  cancelLogin: () => void;
  logout: () => Promise<void>;
}

/** Set by LangSwitcher when the user explicitly picks a language; while present,
 *  the account languagePref no longer overrides the UI language on restore. */
export const LANGUAGE_OVERRIDE_KEY = 'uniportal-language-explicit';

const AuthContext = createContext<AuthContextType | null>(null);

async function tempFetch<T>(path: string, tempToken: string, body?: unknown): Promise<T> {
  const res = await fetch(`/internal/v1${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tempToken}`,
    },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { message?: string; details?: unknown };
    throw new ApiError(res.status, data.message || res.statusText, data.details);
  }

  return res.json() as Promise<T>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const user = state.status === 'authenticated' ? state.user : null;

  // Sync UI language with the account preference on login / session restore —
  // unless the user explicitly switched languages in the UI (their choice wins
  // across reloads and viewer tabs).
  const { i18n } = useTranslation();
  useEffect(() => {
    if (user?.languagePref && !localStorage.getItem(LANGUAGE_OVERRIDE_KEY)) {
      void i18n.changeLanguage(user.languagePref);
    }
  }, [user?.languagePref, i18n]);

  // On mount: silent refresh restores the session (incl. post-SSO ?sso=ok landing)
  useEffect(() => {
    let cancelled = false;

    async function tryRestore() {
      try {
        const res = await fetch('/internal/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (!res.ok) {
          if (!cancelled) setState({ status: 'unauthenticated' });
          return;
        }
        const { accessToken } = (await res.json()) as { accessToken: string };
        setAccessToken(accessToken);

        const { user: authUser } = await apiFetch<{ user: AuthUser }>('/auth/me');
        if (!cancelled) setState({ status: 'authenticated', user: authUser });
      } catch {
        if (!cancelled) setState({ status: 'unauthenticated' });
      }
    }

    void tryRestore();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch('/internal/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
      credentials: 'include',
    });

    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { message?: string };
      throw new ApiError(res.status, data.message || res.statusText);
    }

    const data = (await res.json()) as LoginResponse;

    if (data.step === 'AUTHENTICATED') {
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
    } else if (data.step === 'PASSWORD_EXPIRED') {
      setState({ status: 'password_expired', tempToken: data.tempToken });
    } else if (data.step === 'TOTP_SETUP') {
      setState({ status: 'totp_setup', tempToken: data.tempToken });
    } else {
      setState({ status: 'totp_verify', tempToken: data.tempToken });
    }
  }, []);

  const totpSetup = useCallback(async (): Promise<TotpSetupResponse> => {
    if (state.status !== 'totp_setup') throw new Error('Not in TOTP setup state');
    return tempFetch<TotpSetupResponse>('/auth/totp/setup', state.tempToken);
  }, [state]);

  const totpConfirm = useCallback(
    async (code: string, secret: string): Promise<string[]> => {
      if (state.status !== 'totp_setup') throw new Error('Not in TOTP setup state');
      const data = await tempFetch<TotpConfirmResponse>('/auth/totp/confirm', state.tempToken, {
        code,
        secret,
      });
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
      return data.recoveryCodes;
    },
    [state],
  );

  const totpVerify = useCallback(
    async (code: string, rememberDevice?: boolean) => {
      if (state.status !== 'totp_verify') throw new Error('Not in TOTP verify state');
      const data = await tempFetch<TotpVerifyResponse>('/auth/totp/verify', state.tempToken, {
        code,
        rememberDevice,
      });
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
    },
    [state],
  );

  const recoveryVerify = useCallback(
    async (code: string, rememberDevice?: boolean): Promise<number> => {
      if (state.status !== 'totp_verify') throw new Error('Not in TOTP verify state');
      const data = await tempFetch<RecoveryVerifyResponse>('/auth/recovery/verify', state.tempToken, {
        code,
        rememberDevice,
      });
      setAccessToken(data.accessToken);
      setState({ status: 'authenticated', user: data.user });
      return data.remainingCodes;
    },
    [state],
  );

  // 90-day expired password → set a new one, then continue to the TOTP step
  // signalled by the response (the backend mints a fresh step-bound temp token).
  const forceChangePassword = useCallback(
    async (newPassword: string) => {
      if (state.status !== 'password_expired') throw new Error('Not in password-expired state');
      const data = await tempFetch<{ step: 'TOTP_SETUP' | 'TOTP_VERIFY'; tempToken: string }>(
        '/auth/force-change-password',
        state.tempToken,
        { newPassword },
      );
      if (data.step === 'TOTP_SETUP') {
        setState({ status: 'totp_setup', tempToken: data.tempToken });
      } else {
        setState({ status: 'totp_verify', tempToken: data.tempToken });
      }
    },
    [state],
  );

  const cancelLogin = useCallback(() => {
    setState({ status: 'unauthenticated' });
  }, []);

  const logout = useCallback(async () => {
    // Local sign-out only: clear the UniPortal session (refresh cookie) and
    // return to the login screen. We deliberately do NOT trigger Entra's
    // federated RP-logout — that would sign the user out of their entire
    // Microsoft/Windows account ("pick an account to sign out of"), far beyond
    // leaving UniPortal. Re-signing in via "Sign in with Microsoft" stays smooth
    // because the Microsoft session is untouched.
    try {
      await fetch('/internal/v1/auth/logout', { method: 'POST', credentials: 'include' });
    } catch {
      // logging out regardless
    }
    setAccessToken(null);
    setState({ status: 'unauthenticated' });
  }, []);

  const isPlatformAdmin = user?.role === 'PLATFORM_ADMIN';
  const canPublish =
    isPlatformAdmin || (user?.publishers.some((p) => p.role === 'PUBLISHER_ADMIN') ?? false);

  return (
    <AuthContext.Provider
      value={{
        state,
        user,
        isPlatformAdmin,
        canPublish,
        login,
        totpSetup,
        totpConfirm,
        totpVerify,
        recoveryVerify,
        forceChangePassword,
        cancelLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
